import { Result, ok } from 'neverthrow';

import type {
  CreateLoadBuildingSuggestionRequest,
  CreateLoadBuildingSuggestionResponse,
  CreateSharepointLoadBuildingSuggestionRequest,
} from 'lib/api/createLoadBuildingSuggestion';
import type { ApiError } from 'types/api/ApiError';

export type SharepointPersistRequest =
  | CreateLoadBuildingSuggestionRequest
  | CreateSharepointLoadBuildingSuggestionRequest;

/** Accepts both row and Graph requests, so one wrapper serves Agile and Turvo. */
export type PersistSharepointSuggestion = (
  request: SharepointPersistRequest
) => Promise<Result<CreateLoadBuildingSuggestionResponse, ApiError>>;

export type SharepointPersistCacheEntry = {
  signature: string;
  response: CreateLoadBuildingSuggestionResponse;
  /** Monotonic stamp so an older overlapping attempt cannot clobber a newer commit. */
  attemptId: number;
};

/** Last successful persist per `sourceExternalID`, scoped to one workbook worksheet. */
export type SharepointPersistCache = {
  scopeKey: string;
  entries: Map<string, SharepointPersistCacheEntry>;
  /** In-flight POSTs keyed by `sourceExternalID` + signature so overlapping polls share one call. */
  inflight: Map<
    string,
    Promise<Result<CreateLoadBuildingSuggestionResponse, ApiError>>
  >;
  nextAttemptId: number;
};

export function createSharepointPersistCache(): SharepointPersistCache {
  return {
    scopeKey: '',
    entries: new Map(),
    inflight: new Map(),
    nextAttemptId: 0,
  };
}

function inflightKey(sourceExternalID: string, signature: string): string {
  return `${sourceExternalID}\x1f${signature}`;
}

/** Everything we would POST for a row; any change to it must re-persist. */
export function sharepointPersistRequestSignature(
  request: SharepointPersistRequest
): string {
  return [
    request.sourceCategory,
    request.source,
    request.sourceExternalID,
    request.sourceURL,
    JSON.stringify(request.suggested),
  ].join('\x1f');
}

/**
 * Drop every entry when the workbook/worksheet scope changes; `sourceExternalID` is only
 * unique within a worksheet. Key the scope with `worksheetCacheKey`.
 *
 * Also drops in-flight join keys so a new worksheet does not await a prior sheet's POST.
 * In-flight promises still settle; they skip cache writes when `scopeKey` no longer matches.
 */
export function resetSharepointPersistCacheForScope(
  cache: SharepointPersistCache,
  scopeKey: string
): void {
  if (cache.scopeKey === scopeKey) {
    return;
  }
  cache.scopeKey = scopeKey;
  cache.entries.clear();
  cache.inflight.clear();
}

function commitPersistResult(
  cache: SharepointPersistCache,
  sourceExternalID: string,
  signature: string,
  attemptId: number,
  scopeKeyAtStart: string,
  result: Result<CreateLoadBuildingSuggestionResponse, ApiError>
): void {
  // Worksheet/URL changed while this POST was in flight — do not touch the new scope's cache.
  if (cache.scopeKey !== scopeKeyAtStart) {
    return;
  }

  const current = cache.entries.get(sourceExternalID);
  if (current != null && current.attemptId > attemptId) {
    // A newer overlapping attempt already committed; leave it alone.
    return;
  }

  if (result.isOk() && result.value.suggestionID > 0) {
    cache.entries.set(sourceExternalID, {
      signature,
      response: result.value,
      attemptId,
    });
    return;
  }

  // Signature moved (or this attempt failed): drop our entry / any older one. A newer
  // success was already preserved by the attemptId check above.
  cache.entries.delete(sourceExternalID);
}

/**
 * Wrap a persist call so a row unchanged since its last successful POST reuses that response
 * instead of re-POSTing on every debounced sync.
 *
 * Only persisted suggestions are replayable: a failed persist falls open to a synthetic
 * (negative id) card, which must never be cached as a success or the row would stop reaching
 * the backend. Callers rebuild the card around this response, so paste hints and email id
 * still come from current context on a cache hit.
 *
 * Overlapping polls with the same payload share one in-flight POST. Cache mutations are
 * skipped when the worksheet scope changed mid-flight, and older attempts cannot delete or
 * overwrite a newer successful commit.
 */
export function withSharepointPersistCache(
  persist: PersistSharepointSuggestion,
  cache: SharepointPersistCache
): PersistSharepointSuggestion {
  return async (request) => {
    const signature = sharepointPersistRequestSignature(request);
    const cached = cache.entries.get(request.sourceExternalID);
    if (cached?.signature === signature) {
      return ok(cached.response);
    }

    const joinKey = inflightKey(request.sourceExternalID, signature);
    const existing = cache.inflight.get(joinKey);
    if (existing != null) {
      return existing;
    }

    const scopeKeyAtStart = cache.scopeKey;
    cache.nextAttemptId += 1;
    const attemptId = cache.nextAttemptId;

    const pending = persist(request).then((result) => {
      commitPersistResult(
        cache,
        request.sourceExternalID,
        signature,
        attemptId,
        scopeKeyAtStart,
        result
      );
      return result;
    });
    // Drop the join key only if we still own it (a scope reset may have cleared it).
    void pending.finally(() => {
      if (cache.inflight.get(joinKey) === pending) {
        cache.inflight.delete(joinKey);
      }
    });

    cache.inflight.set(joinKey, pending);
    return pending;
  };
}
